import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import {
  Submission,
  SubmissionService,
  SubmissionFilter,
  SubmissionInput,
  Pagination,
} from "@/utils/api";
import { SortConfig } from "@/components/generic";
import { DEFAULT_PAGE_SIZE } from "@/config";

export interface UseSubmissionsOptions {
  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<SubmissionFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;

  /** Authentication status */
  isAuthenticated?: boolean;
}

export interface UseSubmissionsReturn {
  // Data state
  submission: Submission | null;
  submissions: Submission[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<SubmissionFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isUpdating: boolean;
  removingSubmissionId: string | null;
  selectedSubmission: Submission | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<SubmissionFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<Submission[]>;
  update: (id: string, params: SubmissionInput) => Promise<Submission | null>;
  remove: (id: string) => Promise<boolean>;
  removeMany: (ids: string[]) => Promise<boolean>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Search and filter operations
  setFilters: (filters: Partial<SubmissionFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // UI state management
  setSelectedSubmission: (submission: Submission | null) => void;

  // Helper operations
  reset: () => void;

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [
  { key: "submissionDttm", direction: "desc" },
];

export const useSubmissions = (
  options: UseSubmissionsOptions = {}
): UseSubmissionsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    isAuthenticated = false,
  } = options;

  // Use refs for option values to avoid dependency issues
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);

  // Update refs when options change
  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] =
    useState<Partial<SubmissionFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingSubmissionId, setRemovingSubmissionId] = useState<
    string | null
  >(null);
  const [selectedSubmission, setSelectedSubmission] =
    useState<Submission | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => submissions.length === 0 && !loading,
    [submissions.length, loading]
  );

  // Helper function to update submission in list if it exists, or add it if not
  const updateOrAddSubmissionInList = useCallback(
    (updatedSubmission: Submission) => {
      setSubmissions((prev) => {
        const existingIndex = prev.findIndex(
          (s) => s.id === updatedSubmission.id
        );
        if (existingIndex >= 0) {
          const newSubmissions = [...prev];
          newSubmissions[existingIndex] = updatedSubmission;
          return newSubmissions;
        } else {
          return [updatedSubmission, ...prev];
        }
      });
    },
    []
  );

  // Helper function to remove submission from list if it exists
  const removeSubmissionFromList = useCallback((submissionId: string) => {
    setSubmissions((prev) => prev.filter((s) => s.id !== submissionId));
  }, []);

  // Helper function to remove multiple submissions from list
  const removeSubmissionsFromList = useCallback((submissionIds: string[]) => {
    const idsSet = new Set(submissionIds);
    setSubmissions((prev) => prev.filter((s) => !idsSet.has(s.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    setSubmission(null);
    setSubmissions([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsUpdating(false);
    setRemovingSubmissionId(null);
    setSelectedSubmission(null);
  }, []);

  // Get single submission (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await SubmissionService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const submissionData = result.data[0] as Submission;
          setSubmission(submissionData);
          updateOrAddSubmissionInList(submissionData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load submission";
          setLastError(errorMessage);
          setSubmission(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load submission";
        setLastError(errorMessage);
        setSubmission(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddSubmissionInList]
  );

  // List submissions (matching API method name)
  const list = useCallback(
    async (
      filter: Partial<SubmissionFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        if (reset) {
          setSubmissions([]);
          setCurrentPage(1);
        }
      }

      setFilters(filter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await SubmissionService.list(
          filter as SubmissionFilter,
          paginationParams,
          sortParams
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newSubmissions = Array.isArray(result.data) ? result.data : [];

          if (isAppending) {
            setSubmissions((prev) => {
              const existingIds = new Set(prev.map((s) => s.id));
              const uniqueNewSubmissions = newSubmissions.filter(
                (s: any) => !existingIds.has(s.id)
              );
              return [...prev, ...uniqueNewSubmissions];
            });
          } else {
            setSubmissions(newSubmissions);
          }

          setHasMore(newSubmissions.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load submissions";
          setLastError(errorMessage);
          if (!isAppending) setSubmissions([]);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load submissions";
        setLastError(errorMessage);
        if (!isAppending) setSubmissions([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  // Get many submissions by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<Submission[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await SubmissionService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedSubmissions = Array.isArray(result.data)
            ? result.data
            : [];
          loadedSubmissions.forEach((submission: any) => {
            updateOrAddSubmissionInList(submission);
          });
          setLastError(null);
          return loadedSubmissions;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load submissions";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load submissions";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddSubmissionInList]
  );

  // Update submission (matching API method name)
  const update = useCallback(
    async (id: string, params: SubmissionInput): Promise<Submission | null> => {
      if (!id) {
        setLastError("Submission ID is required for updating");
        return null;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await SubmissionService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedSubmission = result.data[0] as Submission;
          setSubmission(updatedSubmission);
          updateOrAddSubmissionInList(updatedSubmission);
          setLastError(null);
          return updatedSubmission;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to update submission";
          setLastError(errorMessage);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to update submission";
        setLastError(errorMessage);
        return null;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddSubmissionInList]
  );

  // Remove submission (matching API method name)
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (!id) {
        setLastError("Submission ID is required for removal");
        return false;
      }

      setRemovingSubmissionId(id);
      setLastError(null);

      try {
        const result = await SubmissionService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setSubmission(null);
          removeSubmissionFromList(id);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove submission";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove submission";
        setLastError(errorMessage);
        return false;
      } finally {
        setRemovingSubmissionId(null);
      }
    },
    [removeSubmissionFromList]
  );

  // Remove many submissions (matching API method name)
  const removeMany = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (ids.length === 0) {
        setLastError("At least one submission ID is required for removal");
        return false;
      }

      setLastError(null);

      try {
        const result = await SubmissionService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setSubmission(null);
          removeSubmissionsFromList(ids);
          setLastError(null);
          return true;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to remove submissions";
          setLastError(errorMessage);
          return false;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to remove submissions";
        setLastError(errorMessage);
        return false;
      }
    },
    [removeSubmissionsFromList]
  );

  // Load more submissions
  const loadMore = useCallback(async () => {
    if (!loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loadingMore, hasMore, currentPage, filters]);

  // Refresh submissions
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && isAuthenticated && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      list(initialFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, isAuthenticated, hasAutoLoaded, list, pageSize, initialSort]);

  return {
    submission,
    submissions,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    isUpdating,
    removingSubmissionId,
    selectedSubmission,
    get,
    list,
    getMany,
    update,
    remove,
    removeMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    setSelectedSubmission,
    reset,
    lastError,
    clearError,
  };
};
